import { type SetStateAction, useCallback } from "react";

import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Input,
  Message,
} from "semantic-ui-react";
import { styled } from "styled-components";

import { RichText } from "simplydo/react/richtext";

import type { CommentState } from "./Discussion";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 0.5px solid #c9c9c9;
  border-radius: 5px;
  background-color: #fff;
  padding: 6px;
`;

export const MediaContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1px;
  align-items: flex-start;
  justify-content: flex-start;

  margin: 5px 0;
`;

const ActionArea = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 5px 0 5px;
  border-top: 0.5px solid silver;
  padding-top: 7px;
  padding-bottom: 2px;

  > div {
    display: flex;
    align-items: center;
  }
  > .ui.button {
    margin: 0;
  }
`;

type CommentType = CommentState["string"];

const MAX_DISCUSSION_POLL_OPTIONS = 10;

export type DraftDiscussionPoll = {
  _id: string;
  title: string;
  allowMultiSelect: boolean;
  options: Array<{ _id: string; title: string }>;
};

const createDraftId = () =>
  `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

export const validatePolls = (polls: DraftDiscussionPoll[]) => {
  for (const poll of polls) {
    if (!poll.title.trim()) {
      return { valid: false, reason: "Each poll needs a title." };
    }
    if (poll.options.length < 2) {
      return { valid: false, reason: "Each poll needs at least two options." };
    }
    if (poll.options.length > MAX_DISCUSSION_POLL_OPTIONS) {
      return { valid: false, reason: "Each poll can have up to ten options." };
    }

    const seenOptionTitles = new Set<string>();
    for (const option of poll.options) {
      const cleanTitle = option.title.trim().toLowerCase();
      if (!cleanTitle) {
        return { valid: false, reason: "Poll options cannot be empty." };
      }
      if (seenOptionTitles.has(cleanTitle)) {
        return { valid: false, reason: "Poll options must be unique." };
      }
      seenOptionTitles.add(cleanTitle);
    }
  }

  return { valid: true as const };
};
type CommentInputProps = {
  forType: string;
  forId: string;
  currentReply: CommentType;
  updateReply: (
    commentOrUpdater: CommentType | SetStateAction<CommentType>,
  ) => void;
  postComment: () => void;
  discussionId?: string;
  isEditing?: boolean;
  onCancel?: () => void;
  allowPolls?: boolean;
};

export const CommentInput = ({
  forType,
  forId,
  currentReply,
  updateReply,
  postComment,
  isEditing,
  onCancel,
  allowPolls,
}: CommentInputProps) => {
  const { t } = useTranslation();
  const currentPolls = currentReply.polls || [];
  const pollValidation = validatePolls(currentPolls);
  const canSubmit = !!currentReply.messageText?.trim() && pollValidation.valid;

  const updatePoll = useCallback(
    (
      pollId: string,
      pollOrUpdater:
        | DraftDiscussionPoll
        | ((poll: DraftDiscussionPoll) => DraftDiscussionPoll),
    ) => {
      updateReply((prev) => {
        const previousPolls = prev.polls || [];
        const pollIndex = previousPolls.findIndex(
          (poll) => poll._id === pollId,
        );
        if (pollIndex === -1) {
          return prev;
        }

        const existingPoll = previousPolls[pollIndex];
        if (!existingPoll) {
          return prev;
        }
        const updatedPoll =
          typeof pollOrUpdater === "function" ?
            pollOrUpdater(existingPoll)
          : pollOrUpdater;

        const nextPolls = [...previousPolls];
        nextPolls[pollIndex] = updatedPoll;
        return { ...prev, polls: nextPolls };
      });
    },
    [updateReply],
  );

  const removePoll = useCallback(
    (pollId: string) => {
      updateReply((prev) => ({
        ...prev,
        polls: (prev.polls || []).filter((poll) => poll._id !== pollId),
      }));
    },
    [updateReply],
  );

  const addPoll = useCallback(() => {
    updateReply((prev) => ({
      ...prev,
      polls: [
        ...(prev.polls || []),
        {
          _id: createDraftId(),
          title: "",
          allowMultiSelect: false,
          options: [
            { _id: createDraftId(), title: "" },
            { _id: createDraftId(), title: "" },
          ],
        },
      ],
    }));
  }, [updateReply]);

  return (
    <InputContainer>
      <RichText
        forType={forType}
        forId={forId}
        preset="full"
        placeholder="Write a comment..."
        value={currentReply.messageHtml}
        emptyReturnValue={""}
        autoHideMenu={false}
        autoFocus={true}
        disableBorder
        onChange={(html, textValue) =>
          updateReply((prev) => ({
            ...prev,
            messageHtml: html,
            messageText: textValue,
          }))
        }
      />

      {allowPolls && !isEditing ?
        <>
          {(currentReply.polls || []).map((poll, pollIndex) => (
            <div
              key={poll._id}
              style={{
                border: "1px solid #dedede",
                borderRadius: 6,
                padding: 10,
                marginTop: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Header as="h5" style={{ marginBottom: 8 }}>
                  Poll {pollIndex + 1}
                </Header>
                <Button
                  size="mini"
                  basic
                  icon="trash"
                  content="Remove"
                  onClick={() => removePoll(poll._id)}
                />
              </div>

              <Form>
                <Form.Field>
                  <label>Poll title</label>
                  <Input
                    value={poll.title}
                    placeholder="Ask a question..."
                    onChange={(event) =>
                      updatePoll(poll._id, (prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                  />
                </Form.Field>

                <Form.Field>
                  <label>Options</label>
                  {poll.options.map((option, optionIndex) => (
                    <div
                      key={option._id}
                      style={{ display: "flex", gap: 8, marginBottom: 6 }}
                    >
                      <Input
                        fluid
                        value={option.title}
                        placeholder={`Option ${optionIndex + 1}`}
                        onChange={(event) =>
                          updatePoll(poll._id, (prev) => ({
                            ...prev,
                            options: prev.options.map((candidate) =>
                              candidate._id === option._id ?
                                { ...candidate, title: event.target.value }
                              : candidate,
                            ),
                          }))
                        }
                      />
                      {poll.options.length > 2 ?
                        <Button
                          basic
                          icon="close"
                          onClick={() =>
                            updatePoll(poll._id, (prev) => ({
                              ...prev,
                              options: prev.options.filter(
                                (candidate) => candidate._id !== option._id,
                              ),
                            }))
                          }
                        />
                      : null}
                    </div>
                  ))}
                  <Button
                    size="tiny"
                    basic
                    icon="plus"
                    content="Add option"
                    disabled={
                      poll.options.length >= MAX_DISCUSSION_POLL_OPTIONS
                    }
                    onClick={() =>
                      updatePoll(poll._id, (prev) => ({
                        ...prev,
                        options: [
                          ...prev.options,
                          { _id: createDraftId(), title: "" },
                        ],
                      }))
                    }
                  />
                </Form.Field>

                <Form.Group inline style={{ marginTop: 8 }}>
                  <Form.Field>
                    <Checkbox
                      label="Allow multiple selections"
                      checked={poll.allowMultiSelect}
                      onChange={(_e, data) =>
                        updatePoll(poll._id, (prev) => ({
                          ...prev,
                          allowMultiSelect: !!data.checked,
                        }))
                      }
                    />
                  </Form.Field>
                </Form.Group>
              </Form>
            </div>
          ))}
          {!pollValidation.valid ?
            <Message
              size="tiny"
              warning
              icon={<Icon name="warning sign" />}
              content={pollValidation.reason}
            />
          : null}
        </>
      : null}

      <ActionArea>
        <div>
          {allowPolls && !isEditing ?
            <Button
              basic
              compact
              icon="chart bar"
              content={currentPolls.length ? "Add another poll" : "Attach poll"}
              onClick={addPoll}
            />
          : null}
        </div>
        <div>
          {onCancel ?
            <Button basic onClick={onCancel} content={t("generic.cancel")} />
          : null}
          <Button
            basic
            icon={isEditing ? undefined : "send"}
            content={isEditing && t("generic.save")}
            onClick={() => postComment()}
            loading={currentReply.loading || false}
            disabled={!canSubmit}
          />
        </div>
      </ActionArea>
    </InputContainer>
  );
};
